// Per-device memory of the last sticky size + the last sticky color. When
// the user resizes a sticky we stash {w, h}; when the user picks a color we
// stash that. Next click-create reads both back so a board of stickies stays
// visually consistent without the user re-doing the work, while still
// rotating colors so two stickies in a row don't read as the same one.
//
// Scope is global per device (localStorage), not per board. Sized for v1's
// "boards live in the browser anyway" model — moving to per-board storage
// would mean writing to Y.Doc meta, which is overkill for this preference.

import { STICKY_COLORS, type StickyColor } from '@frame/shared';

const SIZE_KEY = 'frame-default-sticky-size';
const COLOR_KEY = 'frame-last-sticky-color';

// Match DEFAULT_STICKY_W / DEFAULT_STICKY_H in tools.ts so a fresh user with
// no remembered size still gets the canonical default.
const FALLBACK: StickySize = { w: 160, h: 160 };

const MIN = 60;
const MAX = 2000;

export interface StickySize {
  w: number;
  h: number;
}

export function readStickySize(): StickySize {
  if (typeof localStorage === 'undefined') return FALLBACK;
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (!raw) return FALLBACK;
    return sanitizeStickySize(JSON.parse(raw));
  } catch {
    return FALLBACK;
  }
}

export function writeStickySize(size: StickySize): void {
  if (typeof localStorage === 'undefined') return;
  const clean = sanitizeStickySize(size);
  try {
    localStorage.setItem(SIZE_KEY, JSON.stringify(clean));
  } catch {
    // Quota exceeded or storage unavailable — silently no-op. The
    // user gets the fallback default on next sticky create.
  }
}

export function sanitizeStickySize(raw: unknown): StickySize {
  if (!raw || typeof raw !== 'object') return FALLBACK;
  const w = (raw as { w?: unknown }).w;
  const h = (raw as { h?: unknown }).h;
  return {
    w: clamp(toNumber(w, FALLBACK.w)),
    h: clamp(toNumber(h, FALLBACK.h)),
  };
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(n: number): number {
  return Math.max(MIN, Math.min(MAX, n));
}

// Last-used sticky color. `nextStickyColor` cycles through the palette so
// every fresh sticky reads as a distinct paper; the picker writes its choice
// back here so future auto-creates resume from the user's pick rather than
// snapping back to the cycle's old position.

export function readStickyColor(): StickyColor | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COLOR_KEY);
    if (!raw) return null;
    return sanitizeStickyColor(raw);
  } catch {
    return null;
  }
}

export function writeStickyColor(color: StickyColor): void {
  if (typeof localStorage === 'undefined') return;
  if (!STICKY_COLORS.includes(color)) return;
  try {
    localStorage.setItem(COLOR_KEY, color);
  } catch {
    // Storage unavailable — silently no-op.
  }
}

// Validate a raw localStorage string against the canonical palette. Any
// unknown / malformed value yields null so the caller falls back to default.
export function sanitizeStickyColor(raw: unknown): StickyColor | null {
  if (typeof raw !== 'string') return null;
  return (STICKY_COLORS as readonly string[]).includes(raw) ? (raw as StickyColor) : null;
}

// Pick the color that follows `prev` in the palette. A null/undefined prev
// returns the first entry so a fresh user starts at the canonical default.
export function nextStickyColor(prev: StickyColor | null | undefined): StickyColor {
  if (!prev) return STICKY_COLORS[0]!;
  const i = STICKY_COLORS.indexOf(prev);
  if (i === -1) return STICKY_COLORS[0]!;
  return STICKY_COLORS[(i + 1) % STICKY_COLORS.length]!;
}
